import React, { createContext, useContext } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  AltRoute,
  CallSplit,
  DataObject,
  DragIndicator,
  ErrorOutline,
  Extension,
  FactCheck,
  HelpOutline,
  InfoOutlined,
  Notes,
  RadioButtonChecked,
  ReportProblemOutlined,
  StopCircleOutlined,
  TableChart,
  TextFields,
  WarningAmber,
} from '@mui/icons-material';
import { Handle, NodeProps, Position } from 'reactflow';

import {
  ANSWER_HEIGHT,
  AnswerKind,
  CHECK_HEIGHT,
  CHECK_SOURCE,
  CHECK_WIDTH,
  EdgeKind,
  HEADER_HEIGHT,
  ICanvasRow,
  IExternalQuestionLayout,
  IFieldCheckLayout,
  IInsertPoint,
  INDENT,
  ISectionLayout,
  ROW_HEIGHT,
  rowKey,
  SECTION_GAP,
  SECTION_TARGET,
  SECTION_WIDTH,
  TERMINAL_HEIGHT,
  TERMINAL_WIDTH,
} from './baukastenModel';
import {
  conditionSummary,
  IEditorNode,
  NODE_KIND_LABEL,
  NodeKind,
  NodePath,
  nodeSummary,
} from './editorModel';
import { FieldLabelResolver } from './fieldPathLabel';
import { IArnoValidationIssue } from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Die Karten des Baukastens (#1245).
 *
 * Jede Karte zeigt ein Stück des kanonischen Modells; was sie anzeigt, kommt fertig aus
 * `baukastenModel` (Zeilen, Höhen, Anschlüsse). Die Karten rechnen nichts selbst aus — sonst
 * stünde ein Anschluss eines Tages woanders als die Linie, die an ihm hängen soll.
 */

// ---------------------------------------------------------------------------
// Farben und Symbole
// ---------------------------------------------------------------------------

export const EDGE_COLOR: Record<EdgeKind, string> = {
  yes: '#2e7d32',
  no: '#c62828',
  option: '#1565c0',
  any: '#00838f',
  field: '#7b1fa2',
};

export const EDGE_LABEL: Record<EdgeKind, string> = {
  yes: 'Ja',
  no: 'Nein',
  option: 'Auswahl',
  any: 'Antwort',
  field: 'Vorgangsdaten',
};

const KIND_COLOR: Record<NodeKind, string> = {
  text: '#546e7a',
  table: '#546e7a',
  data_field: '#3949ab',
  question: '#1565c0',
  conditional_text: '#ef6c00',
  conditional_block_reference: '#ef6c00',
  choice: '#00897b',
  input: '#6a1b9a',
  info: '#78909c',
  block_reference: '#5d4037',
  stop: '#c62828',
  migration_required: '#c62828',
};

const KIND_ICON: Record<NodeKind, React.ReactElement> = {
  text: <Notes fontSize='inherit' />,
  table: <TableChart fontSize='inherit' />,
  data_field: <DataObject fontSize='inherit' />,
  question: <HelpOutline fontSize='inherit' />,
  conditional_text: <CallSplit fontSize='inherit' />,
  conditional_block_reference: <AltRoute fontSize='inherit' />,
  choice: <RadioButtonChecked fontSize='inherit' />,
  input: <TextFields fontSize='inherit' />,
  info: <InfoOutlined fontSize='inherit' />,
  block_reference: <Extension fontSize='inherit' />,
  stop: <StopCircleOutlined fontSize='inherit' />,
  migration_required: <ReportProblemOutlined fontSize='inherit' />,
};

export const kindColor = (kind: NodeKind): string =>
  KIND_COLOR[kind] ?? '#546e7a';
export const kindIcon = (kind: NodeKind): React.ReactElement =>
  KIND_ICON[kind] ?? <Notes fontSize='inherit' />;

const answerColor = (kind: AnswerKind): string => EDGE_COLOR[kind];

/**
 * Anschlüsse liegen über den Zeilen (`zIndex`). Die Zeilen sind zum Verschieben ziehbar und
 * reichen bis an den Kartenrand; lägen sie oben, finge die Zeile den Klick auf den Punkt ab, und
 * aus »Linie ziehen« würde »Zeile verschieben«.
 */
const sourceHandleStyle = (color: string): React.CSSProperties => ({
  width: 12,
  height: 12,
  background: color,
  border: '2px solid #fff',
  boxShadow: `0 0 0 1px ${color}`,
  zIndex: 3,
});

/** Ein Ziel sieht anders aus als eine Quelle: hohl und eckig — hier endet eine Linie. */
const targetHandleStyle: React.CSSProperties = {
  width: 12,
  height: 12,
  background: '#fff',
  border: '2px solid #ef6c00',
  borderRadius: 2,
  zIndex: 3,
};

const hiddenHandleStyle: React.CSSProperties = {
  opacity: 0,
  pointerEvents: 'none',
};

// ---------------------------------------------------------------------------
// Gemeinsamer Zustand der Fläche
// ---------------------------------------------------------------------------

export type BaukastenSelection =
  | { kind: 'section'; sectionIndex: number }
  | { kind: 'node'; sectionIndex: number; path: NodePath }
  | { kind: 'edge'; sectionIndex: number; path: NodePath | null }
  | { kind: 'check'; id: string }
  | { kind: 'external'; questionId: string };

export interface ICardDropIndicator {
  sectionIndex: number;
  point: IInsertPoint;
  markerY: number;
  into: string | null;
}

export interface IBaukastenContext {
  editable: boolean;
  selection: BaukastenSelection | null;
  /** Hervorgehobene Frage: ihre Linien und Ziele leuchten, der Rest tritt zurück. */
  focusQuestion: string | null;
  /** Ziele der hervorgehobenen Frage — Abschnitte (`index`) und Zeilen (`index:pfad`). */
  focusTargets: Set<string>;
  issuesBySection: Map<string, IArnoValidationIssue[]>;
  /** Bedingungen, die ihre eigene Frage verstecken — `index` bzw. `index:pfad`. */
  selfGating: Map<string, string>;
  /** Bedingungen auf eine Frage, die es nicht gibt — `index` bzw. `index:pfad`. */
  danglingConditions: Set<string>;
  questions: { questionId: string; prompt: string }[];
  fieldLabel: FieldLabelResolver;
  cardDrop: ICardDropIndicator | null;
  /** Einfügestelle zwischen Abschnitten beim Ziehen — vor Abschnitt `index` (oder vor dem Ende). */
  gapDrop: { index: number; label: string } | null;
  sectionCount: number;
  checkTargetCount: Map<string, number>;
  selectSection: (_index: number) => void;
  selectNode: (_sectionIndex: number, _path: NodePath) => void;
  selectCheck: (_id: string) => void;
  selectExternal: (_questionId: string) => void;
  startRowDrag: (
    _event: React.DragEvent,
    _sectionIndex: number,
    _path: NodePath
  ) => void;
  endDrag: () => void;
}

export const BaukastenContext = createContext<IBaukastenContext | null>(null);

const useBaukasten = (): IBaukastenContext => {
  const context = useContext(BaukastenContext);
  if (!context) {
    throw new Error('BaukastenContext fehlt');
  }
  return context;
};

export const targetKey = (
  sectionIndex: number,
  path: NodePath | null
): string =>
  path === null ? String(sectionIndex) : `${sectionIndex}:${rowKey(path)}`;

/** Womit eine Zeile beschriftet ist — Datenfelder mit ihrem Namen statt dem Pfad. */
export const rowText = (
  node: IEditorNode,
  fieldLabel: FieldLabelResolver
): string => {
  if (node.type === 'data_field' && node.selector) {
    return node.label || fieldLabel(node.selector) || node.selector;
  }
  return nodeSummary(node);
};

/**
 * Die Einfügestelle zwischen zwei Abschnitten, über der Karte, vor der eingefügt wird.
 *
 * Bewusst kein eigener Knoten der Fläche: ein Knoten, der bei jeder Mausbewegung entsteht und
 * verschwindet, muss jedes Mal neu vermessen werden — genau das löste beim Ziehen die Meldung
 * »ResizeObserver loop completed with undelivered notifications« aus.
 */
const GapMarker: React.FC<{ label: string; left?: number }> = ({
  label,
  left = 0,
}) => (
  <Box
    sx={{
      position: 'absolute',
      top: -(SECTION_GAP / 2 + 14),
      left,
      width: SECTION_WIDTH,
      height: 28,
      border: 2,
      borderStyle: 'dashed',
      borderColor: 'primary.main',
      borderRadius: 2,
      bgcolor: 'rgba(25, 118, 210, 0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      zIndex: 5,
    }}
  >
    <Typography variant='caption' color='primary' sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Abschnitt
// ---------------------------------------------------------------------------

export interface ISectionNodeData {
  layout: ISectionLayout;
}

const SectionRow: React.FC<{ row: ICanvasRow; sectionIndex: number }> = ({
  row,
  sectionIndex,
}) => {
  const context = useBaukasten();
  const { node } = row;
  const selected =
    context.selection?.kind === 'node' &&
    context.selection.sectionIndex === sectionIndex &&
    rowKey(context.selection.path) === row.key;
  const key = targetKey(sectionIndex, row.path);
  const focused = context.focusTargets.has(key);
  const gating = context.selfGating.get(key);
  const dangling = context.danglingConditions.has(key);
  const into =
    context.cardDrop?.sectionIndex === sectionIndex &&
    context.cardDrop.into === row.key;
  const isFocusQuestion =
    node.type === 'question' && node.questionId === context.focusQuestion;
  const color = kindColor(node.type);
  const conditionText =
    row.targetHandleId && node.condition
      ? conditionSummary(node.condition, context.questions, context.fieldLabel)
      : '';

  return (
    <Box
      className='nodrag'
      draggable={context.editable}
      onDragStart={event => context.startRowDrag(event, sectionIndex, row.path)}
      onDragEnd={context.endDrag}
      onClick={event => {
        event.stopPropagation();
        context.selectNode(sectionIndex, row.path);
      }}
      data-baukasten-target={
        row.targetHandleId ? `row|${sectionIndex}|${row.key}` : undefined
      }
      sx={{
        position: 'absolute',
        top: row.y,
        left: 0,
        right: 0,
        height: row.height,
        cursor: context.editable ? 'grab' : 'pointer',
        bgcolor: selected
          ? 'action.selected'
          : into
            ? 'rgba(239, 108, 0, 0.08)'
            : isFocusQuestion || focused
              ? 'rgba(25, 118, 210, 0.06)'
              : 'transparent',
        outline: into ? '2px dashed' : 'none',
        outlineColor: 'warning.main',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
        '&:hover .baukasten-zeilengriff': { opacity: 0.6 },
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={0.75}
        sx={{
          height: ROW_HEIGHT,
          pl: `${8 + row.depth * INDENT}px`,
          pr: row.targetHandleId ? 3 : 1.5,
          borderLeft: row.targetHandleId
            ? `3px solid ${color}`
            : '3px solid transparent',
          ml: row.depth ? `${row.depth * INDENT - 4}px` : 0,
        }}
      >
        <DragIndicator
          className='baukasten-zeilengriff'
          sx={{ fontSize: 14, color: 'text.disabled', opacity: 0, ml: -0.5 }}
        />
        <Box sx={{ color, display: 'flex', fontSize: 16 }}>
          {kindIcon(node.type)}
        </Box>
        <Tooltip
          title={
            conditionText
              ? `${conditionText} — ${rowText(node, context.fieldLabel)}`
              : `${NODE_KIND_LABEL[node.type] ?? node.type}: ${rowText(node, context.fieldLabel)}`
          }
          placement='left'
          enterDelay={600}
        >
          <Typography
            variant='body2'
            noWrap
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontWeight: node.type === 'question' ? 600 : 400,
              color: node.type === 'info' ? 'text.secondary' : 'text.primary',
              fontStyle: node.type === 'info' ? 'italic' : 'normal',
            }}
          >
            {node.type === 'conditional_text' ||
            node.type === 'conditional_block_reference'
              ? `Wenn … ${rowText(node, context.fieldLabel)}`
              : rowText(node, context.fieldLabel)}
          </Typography>
        </Tooltip>
        {(gating || dangling) && (
          <Tooltip
            title={
              gating
                ? `Hängt an der Frage „${gating}", die in diesem Teil selbst steht — sie würde nie gestellt.`
                : 'Die Bedingung verweist auf eine Frage, die es nicht gibt.'
            }
          >
            <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
          </Tooltip>
        )}
      </Stack>
      {row.answers.map(answer => (
        <Stack
          key={answer.handleId}
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          sx={{ height: ANSWER_HEIGHT, pr: 2.5 }}
        >
          <Typography variant='caption' sx={{ color: 'text.secondary', mr: 1 }}>
            ↳
          </Typography>
          <Chip
            size='small'
            label={answer.label}
            variant='outlined'
            sx={{
              height: 18,
              fontSize: 11,
              maxWidth: 220,
              color: answerColor(answer.kind),
              borderColor: answerColor(answer.kind),
            }}
          />
        </Stack>
      ))}
    </Box>
  );
};

export const SectionCard: React.FC<NodeProps<ISectionNodeData>> = ({
  data,
}) => {
  const context = useBaukasten();
  const { layout } = data;
  const { section, index } = layout;
  const selected =
    context.selection?.kind === 'section' &&
    context.selection.sectionIndex === index;
  const conditionSelected =
    context.selection?.kind === 'edge' &&
    context.selection.sectionIndex === index &&
    context.selection.path === null;
  const issues = context.issuesBySection.get(section.sectionId) ?? [];
  const errors = issues.filter(issue => issue.severity === 'ERROR');
  const warnings = issues.filter(issue => issue.severity !== 'ERROR');
  const key = targetKey(index, null);
  const gating = context.selfGating.get(key);
  const dangling = context.danglingConditions.has(key);
  const focused = context.focusTargets.has(key);
  const drop =
    context.cardDrop?.sectionIndex === index ? context.cardDrop : null;

  return (
    <Box
      sx={{
        width: layout.width,
        height: layout.height,
        bgcolor: 'background.paper',
        border: 2,
        borderColor: selected
          ? 'primary.main'
          : errors.length
            ? 'error.main'
            : focused
              ? 'secondary.main'
              : 'divider',
        borderRadius: 2,
        boxShadow: selected ? 6 : 1,
        position: 'relative',
        opacity: section.enabled ? 1 : 0.75,
        borderStyle: section.enabled ? 'solid' : 'dashed',
      }}
    >
      <Handle
        type='target'
        id='seq-in'
        position={Position.Top}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      <Handle
        type='source'
        id='seq-out'
        position={Position.Bottom}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      <Tooltip
        title='Bedingung für den ganzen Abschnitt hier anschließen'
        placement='right'
      >
        <Handle
          type='target'
          id={SECTION_TARGET}
          position={Position.Right}
          isConnectable={context.editable}
          style={{
            ...targetHandleStyle,
            top: HEADER_HEIGHT / 2,
            ...(conditionSelected
              ? { boxShadow: '0 0 0 3px rgba(239,108,0,.35)' }
              : {}),
          }}
        />
      </Tooltip>
      {layout.rows.map(row =>
        row.targetHandleId ? (
          <Handle
            key={`t-${row.key}`}
            type='target'
            id={row.targetHandleId}
            position={Position.Right}
            isConnectable={context.editable}
            style={{ ...targetHandleStyle, top: row.y + ROW_HEIGHT / 2 }}
          />
        ) : null
      )}
      {layout.rows.flatMap(row =>
        row.answers.map(answer => (
          <Handle
            key={`s-${answer.handleId}`}
            type='source'
            id={answer.handleId}
            position={Position.Right}
            isConnectable={context.editable}
            style={{
              ...sourceHandleStyle(answerColor(answer.kind)),
              top: answer.y,
            }}
          />
        ))
      )}

      <Box
        onClick={() => context.selectSection(index)}
        data-baukasten-target={`section|${index}`}
        sx={{
          height: HEADER_HEIGHT,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: selected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          cursor: 'pointer',
        }}
      >
        <Box
          className='baukasten-griff'
          sx={{
            display: 'flex',
            color: 'text.disabled',
            cursor: context.editable ? 'move' : 'default',
          }}
        >
          <DragIndicator fontSize='small' />
        </Box>
        <Box
          sx={{
            minWidth: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: selected ? 'primary.main' : 'grey.300',
            color: selected ? 'primary.contrastText' : 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {index + 1}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant='subtitle2' noWrap>
            {section.title || 'Ohne Titel'}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            noWrap
            component='div'
          >
            {section.condition
              ? conditionSummary(
                  section.condition,
                  context.questions,
                  context.fieldLabel
                )
              : section.enabled
                ? 'immer'
                : 'nicht aktiv — nur über einen Verweis'}
          </Typography>
        </Box>
        {(gating || dangling) && (
          <Tooltip
            title={
              gating
                ? `Der Abschnitt hängt an der Frage „${gating}", die in ihm selbst steht — sie würde nie gestellt.`
                : 'Die Bedingung verweist auf eine Frage, die es nicht gibt.'
            }
          >
            <WarningAmber fontSize='small' color='warning' />
          </Tooltip>
        )}
        {(errors.length > 0 || warnings.length > 0) && (
          <Tooltip
            title={
              <Box component='ul' sx={{ m: 0, pl: 2 }}>
                {issues.slice(0, 8).map((issue, position) => (
                  <li key={`${issue.code}-${position}`}>{issue.message}</li>
                ))}
              </Box>
            }
          >
            <Chip
              size='small'
              icon={errors.length ? <ErrorOutline /> : <WarningAmber />}
              color={errors.length ? 'error' : 'warning'}
              label={errors.length || warnings.length}
              sx={{ height: 22 }}
            />
          </Tooltip>
        )}
      </Box>

      {layout.rows.map(row => (
        <SectionRow key={row.key} row={row} sectionIndex={index} />
      ))}

      {!layout.rows.length && (
        <Box
          sx={{
            position: 'absolute',
            top: HEADER_HEIGHT + 6,
            left: 8,
            right: 8,
            height: 26,
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Elemente hierher ziehen
          </Typography>
        </Box>
      )}

      {drop && !drop.into && (
        <Box
          sx={{
            position: 'absolute',
            top: drop.markerY - 2,
            left: `${6 + drop.point.parentPath.length * INDENT}px`,
            right: 6,
            height: 4,
            borderRadius: 2,
            bgcolor: 'primary.main',
            pointerEvents: 'none',
          }}
        />
      )}

      {context.gapDrop?.index === index && (
        <GapMarker label={context.gapDrop.label} />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Start und Ende
// ---------------------------------------------------------------------------

export const TerminalNode: React.FC<NodeProps<{ label: string }>> = ({
  id,
  data,
}) => {
  const context = useBaukasten();
  const gap =
    id === 'end' && context.gapDrop?.index === context.sectionCount
      ? context.gapDrop
      : null;
  return (
    <Box
      sx={{
        width: TERMINAL_WIDTH,
        height: TERMINAL_HEIGHT,
        borderRadius: TERMINAL_HEIGHT / 2,
        bgcolor: 'grey.800',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: 0.5,
        position: 'relative',
      }}
    >
      <Handle
        type='target'
        id='seq-in'
        position={Position.Top}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      {data.label}
      <Handle
        type='source'
        id='seq-out'
        position={Position.Bottom}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      {gap && (
        <GapMarker
          label={gap.label}
          left={-(SECTION_WIDTH - TERMINAL_WIDTH) / 2}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Prüfung auf Vorgangsdaten
// ---------------------------------------------------------------------------

export const CheckNode: React.FC<NodeProps<{ check: IFieldCheckLayout }>> = ({
  data,
}) => {
  const context = useBaukasten();
  const { check } = data;
  const selected =
    context.selection?.kind === 'check' && context.selection.id === check.id;
  const count = context.checkTargetCount.get(check.id) ?? 0;
  const empty =
    check.condition.left.type === 'selector' && !check.condition.left.selector;
  const color = EDGE_COLOR.field;
  return (
    <Box
      onClick={() => context.selectCheck(check.id)}
      sx={{
        width: CHECK_WIDTH,
        height: CHECK_HEIGHT,
        bgcolor: 'background.paper',
        border: 2,
        borderStyle: check.pending ? 'dashed' : 'solid',
        borderColor: selected ? 'primary.main' : color,
        borderRadius: 2,
        px: 1.25,
        py: 0.75,
        cursor: 'pointer',
        boxShadow: selected ? 6 : 1,
      }}
    >
      <Handle
        type='source'
        id={CHECK_SOURCE}
        position={Position.Left}
        isConnectable={context.editable}
        style={sourceHandleStyle(color)}
      />
      <Stack direction='row' spacing={0.75} alignItems='center'>
        <FactCheck sx={{ fontSize: 16, color }} />
        <Typography variant='caption' sx={{ color, fontWeight: 700 }}>
          Vorgangsdaten
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant='caption' color='text.secondary'>
          {check.pending
            ? 'nicht verbunden'
            : `${count} Ziel${count === 1 ? '' : 'e'}`}
        </Typography>
      </Stack>
      <Typography variant='body2' sx={{ lineHeight: 1.25, mt: 0.25 }} noWrap>
        {empty
          ? 'Feld wählen …'
          : conditionSummary(
              check.condition,
              context.questions,
              context.fieldLabel
            ).replace(/^Wenn /, '')}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Frage aus einem Unterbaustein
// ---------------------------------------------------------------------------

export const ExternalQuestionNode: React.FC<
  NodeProps<{ external: IExternalQuestionLayout }>
> = ({ data }) => {
  const context = useBaukasten();
  const { external } = data;
  const selected =
    context.selection?.kind === 'external' &&
    context.selection.questionId === external.questionId;
  return (
    <Box
      onClick={() => context.selectExternal(external.questionId)}
      sx={{
        width: CHECK_WIDTH,
        height: external.height,
        bgcolor: 'background.paper',
        border: 2,
        borderStyle: 'dotted',
        borderColor: selected ? 'primary.main' : 'info.main',
        borderRadius: 2,
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      {external.answers.map(answer => (
        <Handle
          key={answer.handleId}
          type='source'
          id={answer.handleId}
          position={Position.Left}
          isConnectable={context.editable}
          style={{
            ...sourceHandleStyle(answerColor(answer.kind)),
            top: answer.y,
          }}
        />
      ))}
      <Stack
        direction='row'
        spacing={0.75}
        alignItems='center'
        sx={{ px: 1.25, height: ROW_HEIGHT + 8 }}
      >
        <HelpOutline sx={{ fontSize: 16, color: 'info.main' }} />
        <Typography variant='body2' noWrap sx={{ fontWeight: 600 }}>
          {external.prompt}
        </Typography>
      </Stack>
      {external.answers.map(answer => (
        <Stack
          key={answer.handleId}
          direction='row'
          alignItems='center'
          sx={{ height: ANSWER_HEIGHT, pl: 2.5 }}
        >
          <Chip
            size='small'
            variant='outlined'
            label={answer.label}
            sx={{
              height: 18,
              fontSize: 11,
              color: answerColor(answer.kind),
              borderColor: answerColor(answer.kind),
            }}
          />
        </Stack>
      ))}
    </Box>
  );
};

export const NODE_TYPES = {
  section: SectionCard,
  terminal: TerminalNode,
  check: CheckNode,
  external: ExternalQuestionNode,
};
